import {clsx} from 'clsx'
import {useMemo} from 'react'

import type {MultiSelectFieldConfigFragment$key} from './fields/__generated__/MultiSelectFieldConfigFragment.graphql'
import type {MultiSelectFieldFragment$key} from './fields/__generated__/MultiSelectFieldFragment.graphql'
import {DateField, type DateFieldFragment} from './fields/DateField'
import {IterationField, type IterationFieldFragment} from './fields/IterationField'
import {MultiSelectField} from './fields/MultiSelectField'
import {NumberField, type NumberFieldFragment} from './fields/NumberField'
import {
  SingleSelectField,
  type SingleSelectFieldConfigFragment,
  type SingleSelectFieldFragment,
} from './fields/SingleSelectField'
import {TextField, type TextFieldFragment} from './fields/TextField'
import type {
  ProjectItemNode,
  ProjectIterationFieldNode,
  ProjectNumberFieldValueNode,
  ProjectSingleSelectFieldNode,
  ProjectSingleSelectFieldValueNode,
} from './project-item-section-fields-query-types'
import styles from './ProjectsSections.module.css'

type ProjectItemSectionFieldsProps = {
  projectItem: ProjectItemSectionFieldsFragment
  onIssueUpdate: () => void
}

export type ProjectItemSectionFieldsFragment = {
  readonly id: string
  readonly isArchived: boolean
  readonly project: NonNullable<ProjectItemNode['project']>
  readonly fieldValues: ProjectItemNode['fieldValues'] | null | undefined
}

export function ProjectItemSectionFields({projectItem, onIssueUpdate}: ProjectItemSectionFieldsProps) {
  const {fieldValues} = projectItem

  const fields = useMemo(
    () =>
      (projectItem.project.fields?.edges || [])
        .map(edge => {
          // Issue fields are not rendered in the project item field list until there is a way to edit them and
          // improved GraphQL support.
          if (!edge?.node || edge.node.isIssueField || edge.node.name === 'Status') {
            return
          }

          const field = edge.node
          const dataType = field.dataType

          const fieldId = field.id
          const value =
            (fieldValues?.edges || [])
              .flatMap(e => (e?.node ? [e.node] : []))
              .find(e => {
                return 'field' in e && e.field?.id === fieldId
              }) || null

          const props = {
            viewerCanUpdate: !projectItem.isArchived && projectItem.project.viewerCanUpdate,
            itemId: projectItem.id,
            projectId: projectItem.project.id,
            field,
            value,
            onIssueUpdate,
          }

          switch (dataType) {
            case 'SINGLE_SELECT':
              return (
                <SingleSelectField
                  key={fieldId}
                  {...props}
                  field={field as ProjectSingleSelectFieldNode & SingleSelectFieldConfigFragment}
                  value={value as ProjectSingleSelectFieldValueNode & SingleSelectFieldFragment}
                />
              )
            case 'TEXT':
              return <TextField key={fieldId} {...props} value={value as TextFieldFragment | null} />
            case 'DATE':
              return <DateField key={fieldId} {...props} value={value as DateFieldFragment | null} />
            case 'NUMBER':
              return (
                <NumberField
                  key={fieldId}
                  {...props}
                  field={field}
                  value={value as ProjectNumberFieldValueNode & NumberFieldFragment}
                />
              )
            case 'ITERATION':
              return (
                <IterationField
                  key={fieldId}
                  {...props}
                  field={field as Pick<ProjectIterationFieldNode, 'id' | 'name' | 'configuration'>}
                  value={value as IterationFieldFragment | null}
                />
              )
            case 'MULTI_SELECT': {
              const multiSelectField = field as unknown as {
                multiSelectFieldConfig: MultiSelectFieldConfigFragment$key
              }
              const multiSelectValue = value as unknown as {
                multiSelectFieldValue?: MultiSelectFieldFragment$key | null
              } | null
              return (
                <MultiSelectField
                  key={fieldId}
                  {...props}
                  field={multiSelectField.multiSelectFieldConfig}
                  value={multiSelectValue?.multiSelectFieldValue ?? null}
                />
              )
            }
          }
        })
        .filter(i => i !== undefined),
    [
      projectItem.project.fields?.edges,
      projectItem.project.viewerCanUpdate,
      projectItem.project.id,
      projectItem.isArchived,
      projectItem.id,
      fieldValues?.edges,
      onIssueUpdate,
    ],
  )

  return fields.length === 0 ? (
    <> </>
  ) : (
    <ul className={clsx(styles.FieldListUl, styles.FieldListInternal)}>
      {fields.map((field, index) => (
        // eslint-disable-next-line @eslint-react/no-array-index-key
        <li className={styles.FieldListLi} key={`project-field-${index}`}>
          {field}
        </li>
      ))}
    </ul>
  )
}
